import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const DetailRow = ({ label, value }) => (
  <>
    <Grid item xs={5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography variant="body2">{value ?? "-"}</Typography>
    </Grid>
  </>
);

const formatSignedAmount = (amount, currency, transactionType = "") => {
  if (amount === null || amount === undefined || amount === "") return "-";
  const numeric = Number(amount);
  const signedNumeric =
    Number.isFinite(numeric) && transactionType === "debit" ? -Math.abs(numeric) : numeric;
  const display = Number.isFinite(signedNumeric)
    ? signedNumeric.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : String(amount);
  return currency ? `${display} ${currency}` : display;
};

const AccountTransactionView = ({ open, onClose, data }) => {
  const { t } = useTranslation();

  const typeLabel =
    data?.transaction_type === "credit"
      ? t("accountTransaction.credit")
      : t("accountTransaction.debit");

  const formatDateTime = (value) => {
    if (!value) return "-";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return value;
    return dt.toLocaleString();
  };

  const printRows = [
    [t("accountTransaction.id"), data?.id],
    [t("accountTransaction.account"), data?.account_name || "-"],
    [t("accountTransaction.type"), typeLabel],
    [
      t("accountTransaction.amount"),
      formatSignedAmount(
        data?.amount,
        data?.transaction_currency_title || "",
        data?.transaction_type
      ),
    ],
    [t("accountTransaction.transactionCurrency", "Transaction Currency"), data?.transaction_currency_title || "-"],
    [
      t("accountTransaction.balanceAfter", "Balance After"),
      data?.balance_after != null ? `${data.balance_after} ${data?.transaction_currency_title || ""}`.trim() : "-",
    ],
    [t("accountTransaction.transactionDate"), data?.transaction_date || "-"],
    [t("accountTransaction.description"), data?.description || "-"],
    [t("accountTransaction.accountId", "Account ID"), data?.account || "-"],
    [t("accountTransaction.shop", "Shop"), data?.shop_name || "-"],
    [t("accountTransaction.shopId", "Shop ID"), data?.shop || "-"],
    [t("accountTransaction.createdAt", "Created At"), formatDateTime(data?.created_at)],
    [t("accountTransaction.updatedAt", "Updated At"), formatDateTime(data?.updated_at)],
  ];

  const handlePrint = () => {
    if (!data) return;
    const rowsHtml = printRows
      .map(
        ([label, value]) =>
          `<tr><td class="label">${label}</td><td>${value ?? "-"}</td></tr>`
      )
      .join("");

    const html = `
      <html>
        <head>
          <title>${t("accountTransaction.printTitle")} #${data.id}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
            .title { margin: 0; font-size: 22px; font-weight: 700; }
            .subtitle { margin-top: 4px; color: #6b7280; font-size: 12px; }
            .badge {
              display: inline-block;
              padding: 4px 10px;
              border-radius: 999px;
              font-weight: 700;
              font-size: 12px;
              color: #fff;
              background: ${data?.transaction_type === "credit" ? "#16a34a" : "#dc2626"};
            }
            .summary {
              margin: 14px 0 18px;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 12px;
              background: #f9fafb;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
            }
            .summary-item {
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              padding: 10px;
              background: #fff;
            }
            .summary-item .k { font-size: 11px; color: #6b7280; margin-bottom: 4px; }
            .summary-item .v { font-size: 17px; font-weight: 700; }
            table { width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; }
            td { border: 1px solid #e5e7eb; padding: 8px 10px; vertical-align: top; }
            td.label { width: 34%; font-weight: 600; background: #f3f4f6; }
            .footer { margin-top: 16px; font-size: 11px; color: #6b7280; text-align: right; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h2 class="title">${t("accountTransaction.detailsTitle")}</h2>
              <div class="subtitle">${t("accountTransaction.id")}: #${data.id}</div>
            </div>
            <span class="badge">${typeLabel}</span>
          </div>
          <div class="summary">
            <div class="summary-grid">
              <div class="summary-item">
                <div class="k">${t("accountTransaction.amount")}</div>
                <div class="v" style="color:${data?.transaction_type === "debit" ? "#dc2626" : "#16a34a"}">
                  ${formatSignedAmount(data?.amount, data?.transaction_currency_title || "", data?.transaction_type)}
                </div>
              </div>
              <div class="summary-item">
                <div class="k">${t("accountTransaction.balanceAfter", "Balance After")}</div>
                <div class="v">${data?.balance_after ?? "-"} ${data?.transaction_currency_title || ""}</div>
              </div>
            </div>
          </div>
          <table>
            ${rowsHtml}
          </table>
          <div class="footer">${t("accountTransaction.printedOn", "Printed on")}: ${new Date().toLocaleString()}</div>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("accountTransaction.detailsTitle")}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container rowSpacing={1.25}>
            <DetailRow label={t("accountTransaction.id")} value={data?.id} />
            <DetailRow label={t("accountTransaction.account")} value={data?.account_name} />
            <DetailRow label={t("accountTransaction.type")} value={typeLabel} />
            <DetailRow
              label={t("accountTransaction.amount")}
              value={
                formatSignedAmount(
                  data?.amount,
                  data?.transaction_currency_title || "",
                  data?.transaction_type
                )
              }
            />
            <DetailRow
              label={t("accountTransaction.transactionCurrency", "Transaction Currency")}
              value={data?.transaction_currency_title || "-"}
            />
            <DetailRow
              label={t("accountTransaction.balanceAfter", "Balance After")}
              value={
                data?.balance_after != null
                  ? `${data.balance_after} ${data?.transaction_currency_title || ""}`.trim()
                  : "-"
              }
            />
            <DetailRow
              label={t("accountTransaction.transactionDate")}
              value={data?.transaction_date}
            />
            <DetailRow label={t("accountTransaction.description")} value={data?.description} />
            <DetailRow label={t("accountTransaction.accountId", "Account ID")} value={data?.account} />
            <DetailRow label={t("accountTransaction.shop", "Shop")} value={data?.shop_name} />
            <DetailRow label={t("accountTransaction.shopId", "Shop ID")} value={data?.shop} />
            <DetailRow
              label={t("accountTransaction.createdAt", "Created At")}
              value={formatDateTime(data?.created_at)}
            />
            <DetailRow
              label={t("accountTransaction.updatedAt", "Updated At")}
              value={formatDateTime(data?.updated_at)}
            />
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("accountTransaction.close")}</Button>
        <Button variant="contained" onClick={handlePrint}>
          {t("accountTransaction.print")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountTransactionView;
